export default function BlogContent() {
  return (
    <section className="py-16 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <article className="prose prose-lg max-w-none">
          <div className="text-gray-700 leading-relaxed space-y-6">
            <p className="text-xl text-gray-600 font-medium">
              The BC government's push for purpose-built rentals means speed is the top priority for developers. Every week saved in the "forming" phase is another week of rental income. Relying on a bottlenecked in-house team can slow down an entire project.
            </p>

            <div className="bg-slate-50 border-l-4 border-teal-500 p-6 my-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center">
                <span className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-government-line text-white text-xl"></i>
                </span>
                The 2026 Rental Housing Imperative
              </h2>
              <p className="text-gray-700 mb-4">
                British Columbia's housing crisis has created unprecedented opportunities for purpose-built rental developers:
              </p>
              <ul className="space-y-2 text-gray-700">
                <li><strong>Government Incentives:</strong> BC Housing grants and tax exemptions for rental projects</li>
                <li><strong>Streamlined Approvals:</strong> Fast-tracked permitting for purpose-built rentals</li>
                <li><strong>Strong Demand:</strong> Vancouver vacancy rates below 1% create guaranteed tenancy</li>
                <li><strong>Premium Rents:</strong> New rental units command $2.50-$3.50/sq ft in Metro Vancouver</li>
              </ul>
              <p className="text-gray-700 mt-4 mb-0">
                <strong>The Catch:</strong> Time is money. Every week of construction delay costs thousands in lost rental income. For a 50-unit building at $2,000/month average rent, each week of delay costs <strong>$23,000 in foregone revenue</strong>.
              </p>
            </div>

            <div className="bg-slate-50 border-l-4 border-teal-500 p-6 my-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center">
                <span className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-speed-line text-white text-xl"></i>
                </span>
                The Bottleneck: Formwork Detailing
              </h2>
              <p className="text-gray-700 mb-4">
                Purpose-built rentals typically feature repetitive floor plans—which should make them faster to build. But many developers hit a bottleneck in the formwork detailing phase:
              </p>
              <ul className="space-y-2 text-gray-700">
                <li><strong>In-House Overload:</strong> Your single detailer is overwhelmed by the volume of repetitive layouts</li>
                <li><strong>Revision Delays:</strong> Field changes require quick turnaround that in-house staff can't provide</li>
                <li><strong>Quality Issues:</strong> Rushed work leads to errors that cause site delays</li>
                <li><strong>Coordination Gaps:</strong> Poor communication between design and field teams</li>
              </ul>
              <p className="text-gray-700 mt-4 mb-0">
                The result? Your rental project sits idle while you wait for drawings, burning cash and missing your income targets.
              </p>
            </div>

            <div className="bg-slate-50 border-l-4 border-teal-500 p-6 my-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center">
                <span className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-trophy-line text-white text-xl"></i>
                </span>
                The Dinari Advantage: Built for Repetition
              </h2>
              <p className="text-gray-700 mb-4">
                <strong>Dinari Designs</strong> specializes in the high-efficiency repetition required for rental builds. Here's how we accelerate your project:
              </p>
              <ul className="space-y-2 text-gray-700">
                <li><strong>Template-Based Workflows:</strong> We create master layouts for typical floors, then adapt quickly for variations</li>
                <li><strong>Bluebeam Revu Efficiency:</strong> Digital takeoffs and markup workflows eliminate manual calculation errors</li>
                <li><strong>AutoCAD Precision:</strong> Parametric modeling allows rapid adjustments for field conditions</li>
                <li><strong>Parallel Processing:</strong> We can detail multiple floors simultaneously—something in-house teams can't do</li>
                <li><strong>24-48 Hour Turnaround:</strong> Fast response times keep your crews moving</li>
                <li><strong>Crystal-Clear Instructions:</strong> Site supervisors get unambiguous guidance that eliminates guesswork</li>
              </ul>
              <p className="text-gray-700 mt-4 mb-0">
                We help you build faster, allowing you to move onto the next project while other firms are still waiting for their in-house staff to catch up.
              </p>
            </div>

            <div className="bg-slate-50 border-l-4 border-teal-500 p-6 my-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center">
                <span className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-funds-line text-white text-xl"></i>
                </span>
                Real-World ROI: The Time-to-Income Calculation
              </h2>
              <p className="text-gray-700 mb-4">
                <strong>Project:</strong> 60-unit purpose-built rental in Burnaby
              </p>
              <p className="text-gray-700 mb-4">
                <strong>Scenario A (In-House Detailing):</strong>
              </p>
              <ul className="space-y-2 text-gray-700">
                <li>Formwork detailing takes 16 weeks due to capacity constraints</li>
                <li>Construction completion: Month 18</li>
                <li>First rental income: Month 19</li>
              </ul>
              <p className="text-gray-700 mt-4 mb-4">
                <strong>Scenario B (Dinari Designs):</strong>
              </p>
              <ul className="space-y-2 text-gray-700">
                <li>Formwork detailing takes 10 weeks with our surge capacity</li>
                <li>Construction completion: Month 16</li>
                <li>First rental income: Month 17</li>
              </ul>
              <p className="text-gray-700 mt-4 mb-0">
                <strong>Financial Impact:</strong> 2 months of accelerated rental income = $240,000 (60 units × $2,000/month × 2 months). Our detailing services cost a fraction of that revenue gain.
              </p>
            </div>

            <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl p-8 my-12 text-white">
              <h2 className="text-3xl font-bold mb-4">
                Ready to lean out your overhead?
              </h2>
              <p className="text-lg mb-6 text-teal-50">
                Don't let formwork detailing slow down your rental income. Get <strong>AutoCAD layouts</strong> and <strong>Bluebeam takeoffs</strong> that keep your crews moving and your revenue flowing.
              </p>
              <a 
                href="/home#contact" 
                className="inline-flex items-center px-8 py-4 bg-white text-teal-600 font-semibold rounded-lg hover:bg-teal-50 transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
              >
                Get a Quote
                <i className="ri-arrow-right-line ml-2"></i>
              </a>
            </div>

            <div className="border-t border-gray-200 pt-8 mt-12">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center space-x-4">
                  <span className="text-sm font-medium text-gray-500">Share this article:</span>
                  <div className="flex space-x-3">
                    <a href="#" className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center hover:bg-teal-500 hover:text-white transition-colors cursor-pointer">
                      <i className="ri-linkedin-fill"></i>
                    </a>
                    <a href="#" className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center hover:bg-teal-500 hover:text-white transition-colors cursor-pointer">
                      <i className="ri-twitter-x-fill"></i>
                    </a>
                    <a href="#" className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center hover:bg-teal-500 hover:text-white transition-colors cursor-pointer">
                      <i className="ri-facebook-fill"></i>
                    </a>
                  </div>
                </div>
                <a 
                  href="/knowledge-hub" 
                  className="text-teal-600 hover:text-teal-700 font-medium flex items-center cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line mr-2"></i>
                  Back to Knowledge Hub
                </a>
              </div>
            </div>
          </div>
        </article>
      </div>
    </section>
  );
}
